import { Component, Inject, OnDestroy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Subject, finalize, first, takeUntil } from "rxjs";
import { IExposedUserInOrg, MemberManagementErrorCode } from "../../domain/interfaces/organization.interface";
import { OrganizationService } from "../../domain/services/organization.service";

@Component({
  selector: "app-remove-member-dialog",
  templateUrl: "./remove-member-dialog.component.html",
  styleUrls: ["./remove-member-dialog.component.scss"],
})
export class RemoveMemberDialogComponent implements OnDestroy {
  public isRemoving = false;

  /**
   * Error code returned by the backend for the last removal attempt, shown in the dialog
   */
  public errorCode: MemberManagementErrorCode | null = null;
  protected onDestroy$ = new Subject<void>();

  constructor(
    private readonly organizationService: OrganizationService,
    private readonly translateService: TranslateService,
    public readonly dialogRef: MatDialogRef<RemoveMemberDialogComponent, boolean>,
    private readonly snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public readonly data: { member: IExposedUserInOrg }
  ) {}

  /**
   * Emits onDestroy$ to terminate all active subscriptions.
   */
  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  /**
   * Removes the member from the organization, closing the dialog with `true` on success so the
   * caller reloads the organization. A member no longer in the organization also closes it with
   * `true`, the displayed list being outdated. Known business errors are shown in the dialog.
   */
  confirm(): void {
    this.isRemoving = true;
    this.errorCode = null;

    this.organizationService
      .removeMember(this.data.member.id)
      .pipe(
        first(),
        takeUntil(this.onDestroy$),
        finalize(() => (this.isRemoving = false))
      )
      .subscribe({
        next: () => {
          this.snackBar.open(`✅ ${this.translateService.instant("administration.removeMember.success")}`, "OK", { duration: 5000 });
          this.dialogRef.close(true);
        },
        error: (err) => {
          if ((err.status === 403 || err.status === 409) && Object.values(MemberManagementErrorCode).includes(err.error)) {
            this.errorCode = err.error;
          } else if (err.status === 404) {
            this.snackBar.open(`❗ ${this.translateService.instant("administration.memberManagement.memberNotFound")}`, "OK", {
              duration: 5000,
            });
            this.dialogRef.close(true);
          } else {
            this.snackBar.open(`❗ ${this.translateService.instant("common.error")}`, "OK", { duration: 5000 });
          }
        },
      });
  }
}
